import React, { useMemo } from 'react';

import { Redirect, useLocation } from 'react-router-dom';

/**
 * Single entry for Firebase Auth out-of-band email links when using a custom action URL.
 * Configure in Firebase Console (Authentication → Templates → action URL) to:
 *   https://<your-domain>/auth_email_action
 * Firebase appends: mode, oobCode, apiKey, continueUrl, lang, etc.
 *
 * @see https://firebase.google.com/docs/auth/custom-email-handler
 */
export const AUTH_EMAIL_ACTION_PATH = '/auth_email_action';

/**
 * Merges query string and hash fragments (some clients put params after #).
 */
const mergeActionParams = location => {
  const merged = new URLSearchParams(location.search.replace(/^\?/, ''));
  const hash = location.hash?.slice(1) || '';
  if (hash && !hash.startsWith('/')) {
    const hashQuery = hash.startsWith('?') ? hash.slice(1) : hash;
    const fromHash = new URLSearchParams(hashQuery);
    fromHash.forEach((value, key) => {
      if (!merged.has(key)) {
        merged.set(key, value);
      }
    });
  }
  return merged;
};

const getDecodedUrl = continueUrlRaw => {
  let decoded = continueUrlRaw;
  try {
    decoded = decodeURIComponent(continueUrlRaw);
  } catch {
    // keep raw
  }
  return decoded;
};

const inferModeFromContinueUrl = continueUrlRaw => {
  if (!continueUrlRaw) return null;

  const decoded = getDecodedUrl(continueUrlRaw);
  try {
    const u = new URL(decoded);
    if (u.pathname.includes('email_link')) return 'signin';
    if (u.pathname.includes('reset_password')) return 'resetpassword';
  } catch {
    if (decoded.includes('email_link')) return 'signin';
    if (decoded.includes('reset_password')) return 'resetpassword';
  }
  return null;
};

const targetPathForModeKey = modeKey => {
  switch (modeKey) {
    case 'signin':
      return '/email_link';
    case 'resetpassword':
      return '/reset_password';
    case 'verifyemail':
      return '/signin';
    case 'recoveremail':
      return '/signin';
    default:
      return '/signin';
  }
};

const EmailAuthAction = () => {
  const location = useLocation();
  const { pathname, search } = useMemo(() => {
    const merged = mergeActionParams(location);
    if (!merged.get('mode')) {
      const inferred = inferModeFromContinueUrl(merged.get('continueUrl'));
      if (inferred === 'signin') {
        merged.set('mode', 'signIn');
      }
      if (inferred === 'resetpassword') {
        merged.set('mode', 'resetPassword');
      }
    }
    const modeKey = (merged.get('mode') || '').toLowerCase().replace(/_/g, '');
    const path = targetPathForModeKey(modeKey);
    const qs = merged.toString();
    return { pathname: path, search: qs ? `?${qs}` : '' };
  }, [location.search, location.hash]);

  return <Redirect to={{ pathname, search }} />;
};

export default EmailAuthAction;
